import logger from 'ws-scripts/modules/logger';

const LOG_PREFIX = '[ws-locations-data][LocationFilterManager]';

/**
 * LocationFilterManager — Internal helper that owns all filter state and
 * filtering logic for LocationsData.
 *
 * This class is NOT exposed on window.DDC. LocationsData delegates to it
 * internally so that consumers still call window.DDC.LocationsData.setFilters(),
 * getFilteredLocations(), etc. unchanged.
 *
 * @param {Object} options
 * @param {Function} options.getLocations - Returns the current unfiltered locations array
 * @param {Function} options.publishFilteredLocations - Publishes FILTERED_LOCATIONS_TOPIC
 * @param {Function} options.publishResetMakeFilter - Publishes RESET_MAKE_FILTER_TOPIC
 */
export class LocationFilterManager {
	constructor({
		getLocations,
		publishFilteredLocations,
		publishResetMakeFilter
	}) {
		this._getLocations = getLocations;
		this._publishFilteredLocations = publishFilteredLocations;
		this._publishResetMakeFilter = publishResetMakeFilter;
		this.filters = { makes: [] };

		logger.debug(`${LOG_PREFIX} Initialized`);
	}

	getActiveMakes() {
		return [...this.filters.makes];
	}

	setFilters(filters = {}) {
		const makes = Array.isArray(filters.makes)
			? filters.makes
			: this.filters.makes;

		const currentMakes = this.filters.makes;
		const sorted = (arr) => [...arr].map((m) => m.toLowerCase()).sort();
		const isSame =
			currentMakes.length === makes.length &&
			sorted(currentMakes).every((m, i) => m === sorted(makes)[i]);

		if (isSame) {
			logger.debug(
				`${LOG_PREFIX} setFilters: no change detected, skipping publish`
			);
			return;
		}

		this.filters = { ...this.filters, makes };
		logger.debug(`${LOG_PREFIX} Filters updated:`, {
			filters: this.filters
		});
		this._publishFilteredLocations();
	}

	hasActiveFilters() {
		const { makes } = this.filters;
		return makes && makes.length > 0;
	}

	getFilteredLocations() {
		const locations = this._getLocations();

		if (!this.hasActiveFilters()) {
			return locations;
		}

		const { makes } = this.filters;
		const selectedSet = new Set(makes.map((m) => m.toLowerCase()));
		const result = locations.filter((location) => {
			const { franchiseList } = location;
			if (!Array.isArray(franchiseList) || franchiseList.length === 0) {
				return false;
			}
			return franchiseList.some(
				(franchise) =>
					typeof franchise === 'string' &&
					selectedSet.has(franchise.toLowerCase())
			);
		});

		logger.debug(`${LOG_PREFIX} getFilteredLocations:`, {
			total: locations.length,
			filtered: result.length,
			filters: this.filters
		});

		return result;
	}

	getMakeFacets() {
		const locations = this._getLocations();
		const countMap = {};
		locations.forEach((location) => {
			const { franchiseList } = location;
			if (!Array.isArray(franchiseList)) return;
			new Set(franchiseList).forEach((franchise) => {
				if (typeof franchise === 'string' && franchise.trim()) {
					const key = franchise.trim().toLowerCase();
					if (!countMap[key]) {
						countMap[key] = { label: franchise.trim(), count: 0 };
					}
					countMap[key].count += 1;
				}
			});
		});
		const facets = Object.entries(countMap)
			.map(([value, { label, count }]) => ({ value, label, count }))
			.sort((a, b) => a.label.localeCompare(b.label));
		logger.debug(
			`${LOG_PREFIX} getMakeFacets: ${facets.length} makes derived from ${locations.length} locations`
		);
		return facets;
	}

	reapplyFilters() {
		if (this.hasActiveFilters()) {
			logger.debug(
				`${LOG_PREFIX} Re-applying filters after location update`,
				{ filters: this.filters }
			);
			this._publishFilteredLocations();
		}
	}

	resetMakeFilter() {
		logger.info(`${LOG_PREFIX} Resetting make filter`, {
			previousMakes: this.filters.makes
		});
		this.filters = { ...this.filters, makes: [] };
		try {
			this._publishResetMakeFilter();
		} catch (e) {
			logger.warn(`${LOG_PREFIX} reset-make-filter publish failed:`, {
				error: e.message
			});
		}
		// fire filtered-locations so the count updates immediately
		this._publishFilteredLocations();
	}
}
